import { useOrganizationStats } from './useOrganizationStats'
import { useCurrentOrganization } from './useCurrentOrganization'

export interface SubscriptionLimits {
  maxAssistants: number
  maxAthletes: number
  maxGroups: number
  maxCoaches: number
  tier: string
  tierDisplayName: string
  isLoading: boolean
}

/**
 * Hook to get subscription limits from the current organization.
 * Every user always belongs to an organization, so limits always come from org context.
 */
export function useSubscriptionLimits(): SubscriptionLimits {
  const { data: organization, isLoading: isLoadingOrg } = useCurrentOrganization()
  const { data: organizationStats, isLoading: isLoadingStats } = useOrganizationStats()

  const tier = organization?.subscription_tier || 'team'

  const limits = {
    maxAssistants: organizationStats?.limits.max_assistants ?? 10,
    maxAthletes: organizationStats?.limits.max_athletes ?? 100,
    maxGroups: organizationStats?.limits.max_groups ?? 20,
    maxCoaches: organizationStats?.limits.max_coaches ?? 10,
  }

  // Tier display names
  const tierNames: Record<string, string> = {
    free: 'FREE',
    standard: 'STANDARD',
    pro: 'PRO',
    academy: 'ACADEMY',
    basic: 'BASIC',
    premium: 'PREMIUM',
    team: 'TEAM',
    club: 'CLUB',
    enterprise: 'ENTERPRISE',
  }

  const tierDisplayName = tierNames[tier] || tier.toUpperCase()

  const isLoading = isLoadingOrg || isLoadingStats

  return {
    maxAssistants: limits.maxAssistants,
    maxAthletes: limits.maxAthletes,
    maxGroups: limits.maxGroups,
    maxCoaches: limits.maxCoaches,
    tier,
    tierDisplayName,
    isLoading,
  }
}
